import { Drawer, makeStyles, useMediaQuery } from '@material-ui/core'
import MenuIcon from '@material-ui/icons/Menu'
import { ComponentVersions_3_0, ComponentVersionsInherit, Icon, IconButton, styled, Tooltip } from 'opensolar-ui'
import { setSidebarVisibility, useTranslate } from 'ra-core'
import {
  Children,
  cloneElement,
  isValidElement,
  MouseEvent,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useCollapsibleSidebar } from './menus/hooks/useCollapsibleSidebar'

const RAIL_WIDTH = 72
const EXPANDED_WIDTH = 240
const TRANSITION_MS = 220
const TRANSITION_EASING = 'cubic-bezier(0.4, 0, 0.2, 1)'

/** Gray row with 11px white strip on the right (same layout as former shell + inner). */
const HamburgerMenuShell = styled('div')(({ theme }) => ({
  zIndex: 2,
  flexShrink: 0,
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  display: 'flex',
  justifyContent: 'start',
  alignItems: 'center',
  padding: '16px 20px',
  borderBottom: `1px solid ${theme.palette.neutralGrey[300]}`,
  background: theme.palette.neutralGrey[200],
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

const HamburgerMenuButton = styled(IconButton)({
  padding: '8px',
})

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'relative',
    alignSelf: 'stretch',
    flexShrink: 0,
    overflow: 'visible',
    transition: `width ${TRANSITION_MS}ms ${TRANSITION_EASING}`,
    willChange: 'width',
  },
  panel: {
    // Fixed positioning keeps the sidebar visually pinned below the sticky
    // subheader even when the page / window scrolls. We compute the top
    // offset from the app-header's measured height so the panel always
    // sits flush with the subheader's bottom edge.
    position: 'fixed',
    backgroundColor: theme.palette.background.default,
    borderRight: 'none',
    overflow: 'hidden',
    zIndex: 2,
    transition: `width ${TRANSITION_MS}ms ${TRANSITION_EASING}, box-shadow ${TRANSITION_MS}ms ${TRANSITION_EASING}`,
    willChange: 'width',
  },
  panelFloating: {
    boxShadow: '2px 0 12px rgba(0, 0, 0, 0.12)',
  },
  innerScroll: {
    height: '100%',
    position: 'relative',
    overflowX: 'hidden',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    backgroundColor: theme.palette.common.white,
    // Firefox: thumb, track — track stays white when scrollbar appears on hover.
    scrollbarColor: `transparent ${theme.palette.common.white}`,
    scrollbarWidth: 'none',
    boxSizing: 'border-box',
    '&::-webkit-scrollbar': {
      width: '0px',
    },
    '&::-webkit-scrollbar-track': {
      background: theme.palette.common.white,
    },
    '&::-webkit-scrollbar-thumb': {
      background: 'transparent',
      borderRadius: '4px',
    },
    '&:not(:hover)': {
      scrollbarColor: `transparent ${theme.palette.common.white}`,
    },
    '&:hover': {
      scrollbarWidth: 'thin',
      scrollbarColor: `#888 ${theme.palette.common.white}`,
      '&::-webkit-scrollbar': {
        width: '8px',
      },
      '&::-webkit-scrollbar-thumb': {
        background: '#888',
      },
    },
    // SideBarNavigationMenus keeps its own scroll div unchanged; this panel scrolls instead
    // so the hamburger (sibling above) moves with the menu.
    '& [data-sidebar-scroll-container]': {
      height: 'auto !important',
      maxHeight: 'none !important',
      overflow: 'visible !important',
      overflowY: 'visible !important',
    },
  },
  mobileDrawerPaper: {
    width: EXPANDED_WIDTH,
    backgroundColor: theme.palette.background.default,
  },
}))

interface ShopHoverSidebarProps {
  children?: ReactNode
}

const ShopHoverSidebar = ({ children }: ShopHoverSidebarProps) => {
  const classes = useStyles()
  const translate = useTranslate()
  const dispatch = useDispatch()
  const isXSmall = useMediaQuery<any>((theme) => theme.breakpoints.down('xs'))
  const mobileOpen = useSelector((state: any) => state.admin.ui.sidebarOpen)
  useSelector((state: any) => state.locale) // force redraw on locale change

  const { isLocked, isHovered, isExpanded, toggleLock, setIsHovered, suppressHover, releaseHover } =
    useCollapsibleSidebar()

  const rootRef = useRef<HTMLDivElement | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [panelOffset, setPanelOffset] = useState<{ top: number; left: number }>({ top: 0, left: 0 })
  const [isHamburgerHovered, setIsHamburgerHovered] = useState(false)

  // Measure the app-header (sticky subheader) and the sidebar rail to keep
  // the fixed-position panel aligned with the subheader's bottom edge and
  // the rail's left edge. Re-measures on resize and whenever the header's
  // size changes (e.g. breadcrumb row appears/disappears).
  useEffect(() => {
    const header = document.getElementById('app-header')
    const root = rootRef.current
    if (!root) return

    const update = () => {
      const headerHeight = header ? header.getBoundingClientRect().height : 0
      const rootRect = root.getBoundingClientRect()
      setPanelOffset({ top: headerHeight, left: rootRect.left })
    }

    update()

    const observers: ResizeObserver[] = []
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(update)
      if (header) ro.observe(header)
      ro.observe(root)
      observers.push(ro)
    }
    window.addEventListener('resize', update)
    return () => {
      observers.forEach((ro) => ro.disconnect())
      window.removeEventListener('resize', update)
    }
  }, [])

  // Attach native DOM mouseenter/mouseleave listeners instead of using React
  // synthetic handlers. Some content inside the sidebar — notably the hardware
  // filter panel (Suppliers, Stock Availability, Brand, Warranty, Power) — is
  // rendered via `createPortal` from a different part of the React tree. React
  // synthetic `mouseenter`/`mouseleave` walk the React fiber tree, so moving
  // into that portaled content is treated as leaving the panel even though it
  // is a DOM descendant. Native mouseenter/mouseleave follow DOM ancestry, so
  // they correctly stay "inside" the sidebar while hovering filters.
  useEffect(() => {
    if (isLocked) return
    const node = panelRef.current
    if (!node) return

    const handleEnter = () => setIsHovered(true)
    const handleLeave = () => setIsHovered(false)

    node.addEventListener('mouseenter', handleEnter)
    node.addEventListener('mouseleave', handleLeave)
    return () => {
      node.removeEventListener('mouseenter', handleEnter)
      node.removeEventListener('mouseleave', handleLeave)
    }
  }, [isLocked, setIsHovered])

  const handleMobileClose = () => dispatch(setSidebarVisibility(false))

  const handleToggleLock = useCallback(() => {
    // Keep the sidebar from lingering open when the cursor is still on the
    // hamburger after the user clicks to toggle lock.
    suppressHover()
    toggleLock()
  }, [suppressHover, toggleLock])

  const coordinateHamburgerMouseEnter = useCallback(() => {
    // Only suppress hover-expand when the burger is the cursor's first
    // contact with the sidebar. If the sidebar is already expanded because
    // the user was hovering the menu area below, reaching up to the lock
    // toggle should NOT collapse the sidebar.
    if (!isHovered) {
      suppressHover()
    }
  }, [isHovered, suppressHover])

  const coordinateHamburgerMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      releaseHover()
      // The lock tooltip is rendered in a Popper (portal) — it is not under `panelRef`.
      // If we always call setIsHovered here, moving from the burger to the tooltip would
      // look like "moving to the menu" and incorrectly expand the rail.
      const next = e.relatedTarget
      if (next instanceof Node && panelRef.current?.contains(next)) {
        setIsHovered(true)
      }
    },
    [releaseHover, setIsHovered]
  )

  const onHamburgerStripMouseEnter = useCallback(() => {
    setIsHamburgerHovered(true)
    coordinateHamburgerMouseEnter()
  }, [coordinateHamburgerMouseEnter])

  const onHamburgerStripMouseLeave = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      setIsHamburgerHovered(false)
      coordinateHamburgerMouseLeave(e)
    },
    [coordinateHamburgerMouseLeave]
  )

  if (!isValidElement(children)) {
    return null
  }

  if (isXSmall) {
    const menuWithClose = cloneElement(Children.only(children) as React.ReactElement, {
      onMenuClick: handleMobileClose,
    })
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleMobileClose}
        PaperProps={{ className: classes.mobileDrawerPaper }}
      >
        {menuWithClose}
      </Drawer>
    )
  }

  const menu = cloneElement(Children.only(children) as React.ReactElement, {
    isCollapsed: !isExpanded,
    fitParentHeight: true,
  })

  const isFloating = !isLocked && isExpanded

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <div ref={rootRef} className={classes.root} style={{ width: isLocked ? EXPANDED_WIDTH : RAIL_WIDTH }}>
        <div
          ref={panelRef}
          className={`${classes.panel} ${isFloating ? classes.panelFloating : ''}`}
          style={{
            width: isExpanded ? EXPANDED_WIDTH : RAIL_WIDTH,
            top: panelOffset.top,
            left: panelOffset.left,
            bottom: 0,
          }}
        >
          <div className={classes.innerScroll} style={{ width: EXPANDED_WIDTH }}>
            <HamburgerMenuShell
              data-sidebar-hamburger
              onMouseEnter={onHamburgerStripMouseEnter}
              onMouseLeave={onHamburgerStripMouseLeave}
            >
              <Tooltip
                placement="right"
                title={isLocked ? translate('Unlock sidebar') : translate('Lock sidebar open')}
              >
                <HamburgerMenuButton onClick={handleToggleLock}>
                  {isLocked ? (
                    <MenuIcon />
                  ) : isHamburgerHovered ? (
                    <Icon icon="keyboard_double_arrow_right" />
                  ) : (
                    <MenuIcon />
                  )}
                </HamburgerMenuButton>
              </Tooltip>
            </HamburgerMenuShell>
            {menu}
          </div>
        </div>
      </div>
    </ComponentVersionsInherit>
  )
}

export default ShopHoverSidebar
